import React, { useContext, useEffect, useState } from "react";
import DataContext from "../contexts/DataProvider";
import FormatAmount from "../helper/FormatAmount";
import { FaLock } from "react-icons/fa";

// Shared white header used across ALL screens (checkout, processing, success/failed/pending,
// interstitial) so the top section stays consistent and never drifts.
//
// Row 1: custom logo (customLogoBannerUrl) + "Powered by <name>" when a custom logo is present;
//        otherwise the merchant's own logo (merchant_logo) + merchant name.
// Row 2: ₹amount + label (left) + `right` node (timer pill / status badge) on the right.
//
// customLogoBannerUrl may be any URL incl. a Google Drive link, so it's loaded via a CORS fetch
// → same-origin blob (immune to render-time hotlink 403s), with Drive links expanded to the
// lh3/thumbnail CDN forms. Falls back to a direct <img>, then to the merchant logo.
export const buildBannerCandidates = (url) => {
  if (!url || typeof url !== "string") return [];
  if (/(?:drive|docs)\.google\.com|drive\.usercontent\.google\.com|googleusercontent\.com/.test(url)) {
    const m = url.match(/\/file\/d\/([-\w]+)/) || url.match(/[?&]id=([-\w]+)/) || url.match(/\/d\/([-\w]+)/);
    if (m) {
      const id = m[1];
      return [
        `https://lh3.googleusercontent.com/d/${id}=w1200`,
        `https://drive.google.com/thumbnail?id=${id}&sz=w1200`,
        `https://drive.usercontent.google.com/download?id=${id}&export=view`,
        `https://drive.google.com/uc?export=view&id=${id}`,
        url,
      ];
    }
  }
  return [url];
};

const PaymentHeader = ({
  amountLabel = "PAYABLE AMOUNT",
  labelColor = "#1E56C8",
  right = null,          // node shown at the right of row 2 (timer pill / status badge)
  hideTopRow = false,    // hide row 1 (used when the mobile keyboard is open on checkout)
  compactAmount = false, // smaller amount text (mobile keyboard)
}) => {
  const { data } = useContext(DataContext);
  const [bannerSrc, setBannerSrc] = useState(null);
  const [bannerFailed, setBannerFailed] = useState(false);

  useEffect(() => {
    const candidates = buildBannerCandidates(data?.customLogoBannerUrl);
    setBannerFailed(false);
    setBannerSrc(null);
    if (!candidates.length) return;
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      for (const url of candidates) {
        const controller = new AbortController();
        const t = setTimeout(() => controller.abort(), 8000);
        try {
          const res = await fetch(url, { mode: "cors", signal: controller.signal });
          const ct = res.headers.get("content-type") || "";
          if (res.ok && ct.startsWith("image/")) {
            const blob = await res.blob();
            if (cancelled) return;
            objectUrl = URL.createObjectURL(blob);
            setBannerSrc(objectUrl);
            return;
          }
        } catch (e) {
          // try next candidate, then a direct <img> below
        } finally {
          clearTimeout(t);
        }
      }
      if (!cancelled) setBannerSrc(candidates[0]);
    })();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [data?.customLogoBannerUrl]);

  const customLogoOk = !!data?.customLogoBannerUrl && !bannerFailed;
  const logoSrc = customLogoOk ? bannerSrc : data?.merchant_logo;
  const merchantName = (data?.merchantName || "").replace(/^\s*powered\s+by\s*/i, "");

  return (
    <div className="w-full h-full flex flex-col bg-white px-4">
      {!hideTopRow && (
        <div className="w-full flex-shrink-0 flex items-center justify-between gap-3 pt-3.5 pb-1">
          {/* left: custom logo / framed merchant logo / merchant name / secure fallback */}
          <div className="flex items-center min-w-0" style={{ maxWidth: "62%" }}>
            {logoSrc ? (
              customLogoOk ? (
                // Custom logo: fit within a bounded area (max 48px tall, max full container width),
                // object-contain so ANY size logo scales DOWN to fit without cropping/overflow.
                <div className="flex items-center" style={{ height: 48, maxWidth: "100%" }}>
                  <img
                    src={logoSrc}
                    alt="Merchant"
                    className="max-h-12 max-w-full w-auto object-contain object-left"
                    onError={() => setBannerFailed(true)}
                  />
                </div>
              ) : (
                <div className="h-12 w-12 rounded-[10px] overflow-hidden bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                  <img src={logoSrc} alt="Merchant" className="w-full h-full object-contain" />
                </div>
              )
            ) : merchantName ? (
              <span className="text-sm font-bold text-gray-700 truncate">{merchantName}</span>
            ) : (
              <div className="flex items-center gap-1.5 text-gray-500 text-[10px] font-medium">
                <FaLock style={{ color: "#1E56C8", fontSize: 10 }} /><span>Secure Payment</span>
              </div>
            )}
          </div>
          {/* right: "Powered by <name>" (custom logo) or merchant name (merchant logo) */}
          {customLogoOk ? (
            <span className="text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0">
              Powered by <span className="font-bold text-gray-700">{merchantName}</span>
            </span>
          ) : (
            logoSrc && merchantName && (
              <span className="text-xs font-bold text-gray-700 whitespace-nowrap truncate flex-shrink-0" style={{ maxWidth: "45%" }}>
                {merchantName}
              </span>
            )
          )}
        </div>
      )}
      {/* Row 2: amount (left) + right node */}
      <div className={`flex items-center justify-between flex-1 ${hideTopRow ? "py-1" : ""}`}>
        <div className="flex flex-col">
          <div className={`font-extrabold text-gray-900 leading-none ${compactAmount ? "text-xl" : "text-2xl"}`}>
            ₹{FormatAmount(data?.totalAmount || 0)}
          </div>
          {!hideTopRow && (
            <span className="text-[9px] font-bold tracking-widest mt-0.5" style={{ color: labelColor }}>
              {amountLabel}
            </span>
          )}
        </div>
        {right}
      </div>
    </div>
  );
};

export default PaymentHeader;
